import { useEffect, useRef, useState, type FC } from "react";
import { Document, Page } from "react-pdf";
import TitleSmall from "../../atoms/typography/TitleSmall";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { Col, Row } from "react-grid-system";
import UploadFile from "../../molecules/UploadFiles";
import { useTranslation } from "react-i18next";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import BodySmall from "../../atoms/typography/BodySmall";
import { useTestTemplate } from "../../../graphql/hooks/invoiceInterpretationTemplates/useTestTemplate";
import Alert from "../../molecules/Alert";
import styled from "styled-components";
import Subheading from "../../atoms/typography/Subheading";
import { formatPrice } from "../../../utils/formatters";

interface Props {
  templateId: string;
  clientIdentifier: string;
  onClose: () => void;
}

interface TestTemplateResult {
  errorMessage: string | null;
  maskedFile: {
    content: string; // base64 encoded
    contentType: string;
    name: string;
  } | null;
  extractedData: {
    currencyCode: string | null;
    dueDate: string | null;
    invoiceDate: string | null;
    invoiceNumber: string | null;
    issuerName: string | null;
    issuerOrganizationNumber: string | null;
    issuerReference: string | null;
    issuerVatRegistrationNumber: string | null;
    ocrNumber: string | null;
    orderReference: string | null;
    recipientAddress: string | null;
    recipientAddress2: string | null;
    recipientCity: string | null;
    recipientCountryCode: string | null;
    recipientCustomerNumber: string | null;
    recipientEmail: string | null;
    recipientGlobalLocationNumber: string | null;
    recipientName: string | null;
    recipientOrganizationNumber: string | null;
    recipientReference: string | null;
    recipientVatRegistrationNumber: string | null;
    recipientZipCode: string | null;
    roundingAmount: number | null;
    serviceType: string | null;
    shippingFeeAmountExcludingVat: number | null;
    shippingVatRate: number | null;
    totalAmountExcludingVat: number | null;
    totalAmountIncludingVat: number | null;
    totalVatAmount: number | null;
    vatReverseCharge: boolean | null;
    invoiceLines: Array<{
      articleNumber: string | null;
      description: string | null;
      discountAmount: number | null;
      discountPercentage: number | null;
      quantity: number | null;
      row: number | null;
      totalAmountExcludingVat: number | null;
      unit: string | null;
      unitPriceExcludingVat: number | null;
      vatRate: number | null;
    }>;
  };
}

interface TestTemplateResponse {
  invoiceInterpretation: {
    testTemplate: TestTemplateResult;
  };
}

const ExtractedDataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  border-radius: 4px;
  background-color: ${theme.colors.grey02};
`;

const ResultRow = styled.div`
  padding: 0px 4px;
  border-bottom: 2px solid ${theme.colors.grey05};
  display: flex;
`;

const TestTemplate: FC<Props> = ({ templateId, clientIdentifier, onClose }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [displayResult, setDisplayResult] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestTemplateResult | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined,
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const maskedFile = testResult?.maskedFile;
    if (!maskedFile?.content) return;

    const base64 = maskedFile.content.split(",").pop() || maskedFile.content;
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes.buffer as ArrayBuffer], {
      type: maskedFile.contentType,
    });
    const url = URL.createObjectURL(blob);
    setPdfUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [testResult?.maskedFile]);

  useEffect(() => {
    if (!pdfContainerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0]?.contentRect.width);
    });
    observer.observe(pdfContainerRef.current);
    return () => observer.disconnect();
  }, [displayResult]);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadFile = async (files: File | File[]) => {
    const file = Array.isArray(files) ? files[0] : files;
    setFile(file ?? null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleUploadFile(files[0]);
    }
  };

  const getContentAsBytes = (file: File): Promise<number[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as ArrayBuffer;
        const bytes = new Uint8Array(result);
        resolve(Array.from(bytes));
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    });
  };

  const onCompletedTestTemplate = (result?: TestTemplateResponse) => {
    console.log("Test template result:", result);
    setTestResult(result?.invoiceInterpretation?.testTemplate || null);
    setDisplayResult(true);
  };
  const onErrorTestTemplate = (e?: any) => {
    console.error("Error testing template:", e);
    setErrorMessage(e.message || t("templates.couldNotTestTemplate"));
  };
  const { testTemplate, error, loading } = useTestTemplate({
    onCompleted: onCompletedTestTemplate,
    onError: onErrorTestTemplate,
  });

  const onClickTest = async () => {
    if (!file) return;

    try {
      await testTemplate({
        variables: {
          templateId: templateId,
          clientIdentifier: clientIdentifier,
          input: {
            fileName: file.name,
            content: await getContentAsBytes(file),
          },
        },
      });
    } catch (e) {
      console.error("Error executing testTemplate mutation:", e);
      setErrorMessage(e?.toString() || t("templates.couldNotTestTemplate"));
    }
  };

  return (
    <div>
      <TitleSmall color={theme.colors.primary} textalign="center">
        {displayResult
          ? t("templates.resultFromTest")
          : t("templates.selectFileToTest")}
      </TitleSmall>
      <HorizontalSpacer spacing={2} />
      {!displayResult && (
        <div>
          <Row justify="center">
            <Col md={8}>
              <UploadFile
                text={t("documentHandler.dragAndDropOrClickToUploadDocument")}
                onDrop={handleUploadFile}
                multiple
              />
            </Col>
          </Row>
          <HorizontalSpacer spacing={1} />
          <Row justify="center">
            <div>
              <OutlinedButton
                title={t("documentHandler.orSelectFile")}
                onClick={handleButtonClick}
                medium
                color={theme.colors.primary}
              />
              <input
                type="file"
                accept="application/pdf"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileSelect}
              />
            </div>
          </Row>
          <HorizontalSpacer spacing={2} />
          <div>
            <BodySmall fontweight={500} textalign="center">
              {file?.name}
            </BodySmall>
            <HorizontalSpacer spacing={2} />
          </div>
        </div>
      )}
      {(error || errorMessage) && (
        <Alert
          type="error"
          title="Mallen kund einte testas"
          message={errorMessage || error?.message}
        />
      )}
      {displayResult && (
        <div>
          {testResult?.errorMessage && (
            <Alert type="error" title={testResult.errorMessage} small />
          )}
          <HorizontalSpacer spacing={1} />
          {pdfUrl && (
            <Row>
              <Col lg={5}>
                <ExtractedDataContainer>
                  <Subheading>{t("invoices.issuer")}</Subheading>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.name")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.issuerName}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("common.organisationNumber")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.issuerOrganizationNumber}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.reference")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.issuerReference}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.vatNumber")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.issuerVatRegistrationNumber}
                    </BodySmall>
                  </ResultRow>
                </ExtractedDataContainer>

                <HorizontalSpacer spacing={1} />
                <ExtractedDataContainer>
                  <Subheading>{t("common.recipient")}</Subheading>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.name")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientName}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("common.organisationNumber")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientOrganizationNumber}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("common.email")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientEmail}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.reference")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientReference}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.customerNumber")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientCustomerNumber}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.vatNumber")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {
                        testResult?.extractedData
                          ?.recipientVatRegistrationNumber
                      }
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.addressOne")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientAddress}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.addressTwo")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientAddress2}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.zipCode")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientZipCode}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.city")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientCity}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.countryCode")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.recipientCountryCode}
                    </BodySmall>
                  </ResultRow>
                </ExtractedDataContainer>

                <HorizontalSpacer spacing={1} />
                <ExtractedDataContainer>
                  <Subheading>{t("invoices.invoice")}</Subheading>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.invoiceNumber")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.invoiceNumber}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.invoiceDate")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.invoiceDate}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.dueDate")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.dueDate}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.CurrencyCode")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.currencyCode}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.ocrNumber")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.ocrNumber}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.orderReference")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.orderReference}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.roundOff")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {formatPrice(
                        testResult?.extractedData?.roundingAmount ?? 0,
                      )}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.serviceType")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.serviceType}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.shippingFeeAmountExcludingVat")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {formatPrice(
                        testResult?.extractedData
                          ?.shippingFeeAmountExcludingVat ?? 0,
                      )}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.ShippingFeeVatPercentage")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.shippingVatRate}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.totalExcludingVat")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {formatPrice(
                        testResult?.extractedData?.totalAmountExcludingVat ?? 0,
                      )}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.totalIncludingVat")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {formatPrice(
                        testResult?.extractedData?.totalAmountIncludingVat ?? 0,
                      )}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.totalVat")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {formatPrice(
                        testResult?.extractedData?.totalVatAmount ?? 0,
                      )}
                    </BodySmall>
                  </ResultRow>
                  <ResultRow>
                    <BodySmall style={{ width: "50%" }}>
                      {t("invoices.vatReverseCharge")}
                    </BodySmall>
                    <BodySmall style={{ width: "50%" }}>
                      {testResult?.extractedData?.vatReverseCharge}
                    </BodySmall>
                  </ResultRow>
                </ExtractedDataContainer>
                <HorizontalSpacer spacing={1} />
                {testResult?.extractedData?.invoiceLines &&
                  testResult.extractedData?.invoiceLines?.map((line, index) => (
                    <ExtractedDataContainer
                      key={index}
                      style={{ marginBottom: 16 }}
                    >
                      <Subheading>{t("invoices.invoiceRow")}</Subheading>
                      <HorizontalSpacer spacing={1} />
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.articleNumber")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {line?.articleNumber}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.description")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {line?.description}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.discountAmount")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {line?.discountAmount}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.discountPercentage")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {line?.discountPercentage}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.numberOfAmount")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {line?.quantity}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.rowNumber")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {line?.row}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.unit")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {line?.unit}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.totalExcludingVat")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {formatPrice(line?.totalAmountExcludingVat ?? 0)}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.unitPriceExcludingVat")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {formatPrice(line?.unitPriceExcludingVat ?? 0)}
                        </BodySmall>
                      </ResultRow>
                      <ResultRow>
                        <BodySmall style={{ width: "50%" }}>
                          {t("invoices.vatPercentage")}
                        </BodySmall>
                        <BodySmall style={{ width: "50%" }}>
                          {line?.vatRate}
                        </BodySmall>
                      </ResultRow>
                    </ExtractedDataContainer>
                  ))}
              </Col>
              <Col lg={7}>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <OutlinedButton
                    title={t("documentHandler.previousPage")}
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage <= 1}
                    color={theme.colors.text}
                  />
                  <BodySmall>
                    {t("common.page")} {currentPage} av {numPages}
                  </BodySmall>
                  <OutlinedButton
                    title={t("documentHandler.nextPage")}
                    onClick={() =>
                      setCurrentPage((p) =>
                        numPages ? Math.min(p + 1, numPages) : p + 1,
                      )
                    }
                    disabled={numPages ? currentPage >= numPages : true}
                    color={theme.colors.text}
                  />
                </div>
                <div
                  ref={pdfContainerRef}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={({ numPages }) => {
                      setNumPages(numPages);
                      setCurrentPage(1);
                    }}
                    onLoadError={(err) => console.error("PDF load error:", err)}
                  >
                    <Page
                      pageNumber={currentPage}
                      width={containerWidth}
                      devicePixelRatio={window.devicePixelRatio}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                </div>
              </Col>
            </Row>
          )}
          <HorizontalSpacer spacing={2} />
        </div>
      )}
      <div
        style={{
          display: "flex",
          gap: 24,
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <DefaultButton title={t("common.close")} onClick={onClose} />
        {!displayResult && (
          <ActionButton
            color={theme.colors.primary}
            title={t("templates.test")}
            onClick={onClickTest}
            disabled={!file}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

export default TestTemplate;
